import React, {useState, useEffect, useCallback} from 'react';
import {Box, HStack, Text} from 'native-base';
import MultiSelectFilter from './MultiSelectFilter';
import Trans from './Trans';
import APIAction from '../../Actions/APIAction';

/**
 * Shared filter component for Project and Department filters
 * Used in ProjectListScreen and ApproveJobs
 *
 * @param {Array} projects - Array of project objects with {id, name} or just names
 * @param {Function} onChange - Callback with {selectedProjects, selectedDepartments}
 * @param {boolean} showLabel - Whether to show "Filters" label (default: true)
 */
const ProjectDepartmentFilters = ({projects = [], onChange, showLabel = true}) => {
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      const response = await APIAction.request({
        method: 'GET',
        url: '/api/departments',
        cache: true,
      });
      if (response && response['hydra:member']) {
        setDepartments(response['hydra:member']);
      }
    };
    fetchDepartments();
  }, []);

  // Notify parent when filters change
  const notifyChange = useCallback((newProjects, newDepartments) => {
    if (onChange) {
      onChange({
        selectedProjects: newProjects,
        selectedDepartments: newDepartments,
      });
    }
  }, [onChange]);

  const handleProjectChange = useCallback((values) => {
    setSelectedProjects(values);
    notifyChange(values, selectedDepartments);
  }, [notifyChange, selectedDepartments]);

  const handleDepartmentChange = useCallback((values) => {
    setSelectedDepartments(values);
    notifyChange(selectedProjects, values);
  }, [notifyChange, selectedProjects]);

  // Get unique project names for dropdown options
  const projectOptions = [...new Set(
    projects.map(p => typeof p === 'string' ? p : p.name)
  )].map(name => ({
    id: name,
    name: name,
  }));

  // Get unique department names with "NONE" option
  const departmentOptions = [
    {id: 'none', name: 'NONE'},
    ...[...new Set(departments.map(d => d.name))].map(name => ({
      id: name,
      name: name,
    })),
  ];

  return (
    <Box backgroundColor={'#fff'} borderRadius={'md'} p={3}>
      {showLabel && (
        <Text fontWeight="600" mb={2}>
          <Trans>Filters</Trans>
        </Text>
      )}
      <HStack space={3}>
        <Box flex={1}>
          <MultiSelectFilter
            label="Project"
            placeholder="Select..."
            options={projectOptions}
            selectedValues={selectedProjects}
            onChange={handleProjectChange}
          />
        </Box>
        <Box flex={1}>
          <MultiSelectFilter
            label="Departement"
            placeholder="Select..."
            options={departmentOptions}
            selectedValues={selectedDepartments}
            onChange={handleDepartmentChange}
          />
        </Box>
      </HStack>
    </Box>
  );
};

export default ProjectDepartmentFilters;
